import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    ConfiguratorDataViewService,
    ConfiguratorLandingPageService,
    ConfiguratorNewsletterService,
    ConfiguratorSaveQueryService,
    ConfiguratorScenarioService,
    ConfiguratorTableService,
    ConfiguratorTemplateHtmlService,
    EngineCampaignService,
    SaveQuery
} from '@tj/tj-dmp-lib-api';

import { TransferableType } from './transfer.model';

/** {@code SaveQuery.code} exists on the Java entity (SaveQueryTransferHandler's business key)
 *  but is missing from the generated TS model, which predates it. */
interface SaveQueryLite extends SaveQuery {
    code: string;
}

export interface TransferPickItem {
    code: string;
    name: string;
}

/** Which transferable types this screen scopes to a single affiliate before listing (the
 *  generated client's Scenario listing requires a partner; the others are optional filters). */
export const PARTNER_SCOPED_TYPES: ReadonlySet<TransferableType> = new Set(['scenario']);

/** Which transferable types support an optional affiliate filter, on top of the mandatory ones above. */
export const PARTNER_FILTERABLE_TYPES: ReadonlySet<TransferableType> = new Set([
    'scenario',
    'newsletter',
    'landingpage',
    'savequery'
]);

/**
 * Routes each transferable type (as defined by dmp-configurator's TransferHandler.getTransferType())
 * to the existing, already-up-to-date generated-client listing call for that domain, and normalizes
 * the result to {code, name} - code always matching the business key the matching TransferHandler
 * uses (see transfer.model.ts / dmp-configurator handler classes).
 */
@Injectable({ providedIn: 'root' })
export class TransferObjectSourceService {
    constructor(
        private tableService: ConfiguratorTableService,
        private dataViewService: ConfiguratorDataViewService,
        private campaignService: EngineCampaignService,
        private scenarioService: ConfiguratorScenarioService,
        private newsletterService: ConfiguratorNewsletterService,
        private landingPageService: ConfiguratorLandingPageService,
        private templateHtmlService: ConfiguratorTemplateHtmlService,
        private saveQueryService: ConfiguratorSaveQueryService
    ) {}

    listObjects(type: TransferableType, sourceTenant: string, partner?: string): Observable<TransferPickItem[]> {
        switch (type) {
            case 'table':
                return this.tableService
                    .getTablesUsingGET(sourceTenant)
                    .pipe(map(items => items.map(t => ({ code: t.code, name: t.label || t.code }))));

            case 'dataview':
                return this.dataViewService
                    .getAllDataViewsUsingGET(sourceTenant)
                    .pipe(map(items => items.map(dv => ({ code: dv.code, name: dv.name || dv.code }))));

            case 'campaign':
                return this.campaignService
                    .findAll(sourceTenant, partner || undefined)
                    .pipe(map(items => items.map(c => ({ code: c.code, name: c.name || c.code }))));

            case 'scenario':
                if (!partner) {
                    return of([]);
                }
                return this.scenarioService
                    .listOrLatest(sourceTenant, partner)
                    // ScenarioTransferHandler's business key is Scenario.name, not scenarioId.
                    .pipe(map(items => items.map(s => ({ code: s.name, name: s.name }))));

            case 'newsletter':
                return this.newsletterService
                    .getNewslettersUsingGET(sourceTenant, 'FR', 'FR', partner || undefined)
                    .pipe(map(items => items.map(n => ({ code: n.uuid, name: n.name || n.uuid }))));

            case 'landingpage':
                return this.landingPageService
                    .getAllLandingPagesUsingGET(sourceTenant, undefined, undefined, undefined, partner || undefined)
                    .pipe(map(items => items.map(lp => ({ code: lp.code, name: lp.name || lp.code }))));

            case 'templatehtml':
                return this.templateHtmlService
                    .getAllTemplateHtmlsUsingGET(sourceTenant)
                    .pipe(map(items => items.map(t => ({ code: t.code, name: t.name || t.code }))));

            case 'savequery':
                return this.saveQueryService
                    .getAllSaveQuerysByDatastoreUsingGET(sourceTenant, partner || undefined)
                    .pipe(
                        map(items =>
                            (items as SaveQueryLite[]).map(sq => ({ code: sq.code, name: sq.name || sq.code }))
                        )
                    );

            default:
                return of([]);
        }
    }
}
